import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { formatFechaHora } from "../../lib/dates";
import { Button } from "../../components/ui/Button";
import { Table } from "../../components/ui/Table";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { Badge } from "../../components/ui/Badge";
import { RedsysRecoveryPanel } from "../../components/pago/RedsysRecoveryPanel";

interface CobroMitRow {
  clienteId: number;
  razonSocial: string;
  numPilotos: number;
  importeTotal: number;
  pendientesEmitir: number;
  ultimoFallo: { fecha: string; motivo: string } | null;
  intentosRechazados: number;
  requiereRevision: boolean;
}

interface CobroIncidenciaRow {
  id: number;
  clienteId: number;
  razonSocial: string;
  orderId: string | null;
  importeTotal: number;
  numPilotos: number;
  estado: string;
  dsResponse: string | null;
  errorDetalle: string | null;
  createdAt: string;
  devolucionEstado: string;
  devolucionError: string | null;
  devolucionIntentos: number;
  requiereDevolucion: boolean;
}

function formatMoneda(value: number) {
  return `${Number(value).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

export function CobrosMitPage() {
  const [rows, setRows] = useState<CobroMitRow[]>([]);
  const [incidencias, setIncidencias] = useState<CobroIncidenciaRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [retryLoadingId, setRetryLoadingId] = useState<number | null>(null);
  const [transferLoadingId, setTransferLoadingId] = useState<number | null>(null);
  const [transferConfirmId, setTransferConfirmId] = useState<number | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [incidentLoadingId, setIncidentLoadingId] = useState<number | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: CobroMitRow[]; incidencias: CobroIncidenciaRow[] }>(
        "/admin/cobros-mit"
      );
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setIncidencias(Array.isArray(res.data?.incidencias) ? res.data.incidencias : []);
    } catch {
      setRows([]);
      setIncidencias([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const handleReintentar = async (clienteId: number) => {
    setRetryLoadingId(clienteId);
    setActionMessage(null);
    try {
      const res = await api.post<{ success: boolean; dsResponse?: string; error?: string }>(
        `/admin/cobros-mit/reintentar/${clienteId}`
      );
      setActionMessage({
        type: "ok",
        text: res.data.dsResponse
          ? `Cobro realizado correctamente (Redsys: ${res.data.dsResponse})`
          : "Cobro realizado correctamente",
      });
      await fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; dsResponse?: string } } };
      const msg =
        ax.response?.data?.error ??
        (ax.response?.data?.dsResponse
          ? `Cobro rechazado (${ax.response.data.dsResponse})`
          : "No se pudo reintentar el cobro");
      setActionMessage({ type: "error", text: msg });
    } finally {
      setRetryLoadingId(null);
    }
  };

  const handleMarcarTransferencia = async () => {
    if (transferConfirmId == null) return;
    const clienteId = transferConfirmId;
    setTransferLoadingId(clienteId);
    setActionMessage(null);
    try {
      await api.post(`/admin/cobros-mit/marcar-transferencia/${clienteId}`);
      setActionMessage({
        type: "ok",
        text: "Cobro marcado como pagado por transferencia",
      });
      setTransferConfirmId(null);
      await fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setActionMessage({
        type: "error",
        text: ax.response?.data?.error ?? "No se pudo marcar el cobro",
      });
    } finally {
      setTransferLoadingId(null);
    }
  };

  const resolveIncident = async (
    row: CobroIncidenciaRow,
    resolucion: "confirmado" | "no_cobrado"
  ) => {
    const nota = window.prompt(
      resolucion === "confirmado"
        ? "Indica cómo se verificó que Redsys sí realizó el cobro:"
        : "Indica cómo se verificó que Redsys no realizó el cobro:"
    )?.trim();
    if (!nota) return;
    setIncidentLoadingId(row.id);
    setActionMessage(null);
    try {
      await api.post(`/admin/cobros-mit/operaciones/${row.id}/resolver`, {
        resolucion,
        nota,
      });
      setActionMessage({
        type: "ok",
        text:
          resolucion === "confirmado"
            ? "Cobro confirmado; los pilotos quedan protegidos frente a un segundo cargo."
            : "Operación marcada como no cobrada; los pilotos pueden volver a cobrarse.",
      });
      await fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setActionMessage({
        type: "error",
        text: ax.response?.data?.error ?? "No se pudo resolver la operación.",
      });
    } finally {
      setIncidentLoadingId(null);
    }
  };

  const retryRefund = async (row: CobroIncidenciaRow) => {
    setIncidentLoadingId(row.id);
    setActionMessage(null);
    try {
      await api.post(`/admin/cobros-mit/operaciones/${row.id}/reintentar-devolucion`);
      setActionMessage({ type: "ok", text: "Devolución confirmada por Redsys." });
      await fetchList();
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: string; result?: { dsResponse?: string } } };
      };
      setActionMessage({
        type: "error",
        text:
          ax.response?.data?.error ??
          (ax.response?.data?.result?.dsResponse
            ? `La devolución no se confirmó (${ax.response.data.result.dsResponse}).`
            : "La devolución sigue sin confirmarse."),
      });
      await fetchList();
    } finally {
      setIncidentLoadingId(null);
    }
  };

  const resolveExternalRefund = async (row: CobroIncidenciaRow) => {
    const nota = window.prompt(
      "Indica la referencia y cómo se comprobó la devolución realizada fuera de la plataforma:"
    )?.trim();
    if (!nota) return;
    setIncidentLoadingId(row.id);
    setActionMessage(null);
    try {
      await api.post(
        `/admin/cobros-mit/operaciones/${row.id}/resolver-devolucion-externa`,
        { nota }
      );
      setActionMessage({
        type: "ok",
        text: "Devolución externa registrada; los pilotos vuelven a estar pendientes.",
      });
      await fetchList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setActionMessage({
        type: "error",
        text: ax.response?.data?.error ?? "No se pudo registrar la devolución externa.",
      });
    } finally {
      setIncidentLoadingId(null);
    }
  };

  const columns = [
    {
      key: "cliente",
      header: "Cliente",
      render: (r: CobroMitRow) => r.razonSocial,
    },
    {
      key: "numPilotos",
      header: "Nº Pilotos",
      render: (r: CobroMitRow) => r.numPilotos,
    },
    {
      key: "importe",
      header: "Importe",
      render: (r: CobroMitRow) => formatMoneda(r.importeTotal),
    },
    {
      key: "pendientesEmitir",
      header: "Certificados pendientes",
      render: (r: CobroMitRow) =>
        r.pendientesEmitir > 0 ? (
          <Badge className="bg-orange-100 text-orange-800">{r.pendientesEmitir}</Badge>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "ultimoFallo",
      header: "Último fallo",
      render: (r: CobroMitRow) =>
        r.ultimoFallo ? (
          <div className="text-sm">
            <div className="text-gray-700">{formatFechaHora(r.ultimoFallo.fecha)}</div>
            <div className="text-gray-500">{r.ultimoFallo.motivo}</div>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: "intentos",
      header: "Intentos",
      render: (r: CobroMitRow) =>
        r.requiereRevision ? (
          <Badge variant="warning">{r.intentosRechazados} · revisión manual</Badge>
        ) : (
          r.intentosRechazados
        ),
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (r: CobroMitRow) => (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            loading={retryLoadingId === r.clienteId}
            onClick={() => void handleReintentar(r.clienteId)}
          >
            Reintentar cobro
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={transferLoadingId === r.clienteId}
            onClick={() => setTransferConfirmId(r.clienteId)}
          >
            Marcar transferencia
          </Button>
        </div>
      ),
    },
  ];

  const incidentColumns = [
    {
      key: "cliente",
      header: "Cliente",
      render: (r: CobroIncidenciaRow) => r.razonSocial,
    },
    {
      key: "pedido",
      header: "Pedido",
      render: (r: CobroIncidenciaRow) => r.orderId ?? `Cobro #${r.id}`,
    },
    {
      key: "importe",
      header: "Importe",
      render: (r: CobroIncidenciaRow) => formatMoneda(r.importeTotal),
    },
    {
      key: "estado",
      header: "Incidencia",
      render: (r: CobroIncidenciaRow) =>
        r.requiereDevolucion ? (
          <div>
            <Badge variant="danger">Devolución {r.devolucionEstado}</Badge>
            <p className="mt-1 max-w-xs text-xs text-gray-500">{r.devolucionError}</p>
          </div>
        ) : (
          <div>
            <Badge variant="warning">Cobro {r.estado}</Badge>
            <p className="mt-1 max-w-xs text-xs text-gray-500">
              {r.errorDetalle ?? r.dsResponse ?? "Resultado pendiente de verificar"}
            </p>
          </div>
        ),
    },
    {
      key: "acciones",
      header: "Acciones seguras",
      render: (r: CobroIncidenciaRow) => (
        <div className="flex flex-wrap gap-2">
          {r.requiereDevolucion ? (
            <>
              <Button
                size="sm"
                loading={incidentLoadingId === r.id}
                onClick={() => void retryRefund(r)}
              >
                Reintentar devolución
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={incidentLoadingId === r.id}
                onClick={() => void resolveExternalRefund(r)}
              >
                Registrar devolución externa
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                loading={incidentLoadingId === r.id}
                onClick={() => void resolveIncident(r, "confirmado")}
              >
                Sí se cobró
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={incidentLoadingId === r.id}
                onClick={() => void resolveIncident(r, "no_cobrado")}
              >
                No se cobró
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-[#2C2C2C]">Cobros pendientes</h1>
        <Button variant="secondary" onClick={() => void fetchList()}>
          Actualizar
        </Button>
      </div>

      {actionMessage && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            actionMessage.type === "ok"
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {incidencias.length > 0 && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-red-700">Operaciones que requieren revisión</h2>
            <p className="text-sm text-gray-500">
              Estos pilotos están bloqueados para impedir un cobro duplicado hasta resolver la
              operación.
            </p>
          </div>
          <Table<CobroIncidenciaRow> columns={incidentColumns} data={incidencias} />
        </section>
      )}

      {!loading && rows.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-white py-8 text-center text-gray-500">
          No hay cobros pendientes
        </p>
      ) : (
        <Table<CobroMitRow> columns={columns} data={rows} loading={loading} />
      )}

      <RedsysRecoveryPanel endpoint="/admin/cobros-mit/redsys/reprocesar" admin />

      <ConfirmDialog
        isOpen={transferConfirmId != null}
        onClose={() => setTransferConfirmId(null)}
        onConfirm={() => void handleMarcarTransferencia()}
        title="Marcar como pagado por transferencia"
        message="Se marcarán todos los pilotos pendientes de este cliente como cobrados y se generará el recibo si procede. ¿Continuar?"
        confirmLabel="Confirmar"
        variant="primary"
      />

    </div>
  );
}
